/**
 * Webtech HR Engine™ - Email Verification Page
 * Page de vérification de l'email avec code à 6 chiffres
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles,
  Mail,
  Loader2,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
  Shield
} from 'lucide-react';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Countdown timer for resend
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value.slice(-1); // Take only last character
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits are entered
    if (index === 5 && value && newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData) {
      const newCode = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setCode(newCode);
      
      // Focus last filled input or first empty one
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
      
      // Auto-submit if complete
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (verificationCode = code.join('')) => {
    if (verificationCode.length !== 6) {
      toast.error('Veuillez saisir le code à 6 chiffres complet');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-email', {
        email,
        code: verificationCode
      });
      
      setVerified(true);
      toast.success('Compte créé avec succès !');
      
      // Store token for later use if provided
      if (response.data.token) {
        // Save to localStorage for auto-login when they click the button
        localStorage.setItem('pendingToken', response.data.token);
        localStorage.setItem('pendingUser', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Verification error:', error);
      const message = error.response?.data?.detail || 'Code invalide ou expiré';
      const redirect = error.response?.data?.redirect;
      
      // Handle case where account already exists - show success modal instead of error
      if (redirect === 'login' || message.includes('existe déjà') || message.includes('vous connecter')) {
        setVerified(true);
        toast.success('Votre compte est déjà actif !');
        return;
      }
      
      if (redirect === 'register') {
        toast.error(message);
        setTimeout(() => navigate('/register'), 3000);
        return;
      }
      
      toast.error(message);
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('Un nouveau code a été envoyé à votre adresse email');
      setCountdown(60); // 60 seconds cooldown
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error('Resend error:', error);
      const message = error.response?.data?.detail || "Erreur lors de l'envoi du code";
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Compte validé !</h1>
            <p className="text-xl font-semibold text-webtech-blue mb-4">
              Bienvenue sur HR Engine
            </p>
            <p className="text-slate-600 mb-8">
              Votre compte a été créé avec succès. Vous pouvez maintenant accéder à votre espace.
            </p>
            <Link to="/login">
              <Button className="w-full bg-webtech-blue hover:bg-webtech-blue-hover text-white py-3 text-lg">
                Cliquez ici pour vous connecter
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="py-4 px-4 sm:px-6 lg:px-8 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-webtech-blue to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading font-bold text-xl text-slate-900">Webtech</span>
              <span className="font-heading font-bold text-xl text-webtech-blue ml-1">HR Engine™</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-webtech-blue to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Vérifiez votre email
              </CardTitle>
              <CardDescription className="text-base">
                Nous avons envoyé un code de vérification à 6 chiffres à
                <br />
                <span className="font-medium text-slate-700">{email}</span>
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6">
              {/* Code Input */}
              <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-webtech-blue focus:ring-webtech-blue/20"
                    disabled={loading}
                  />
                ))}
              </div>

              <p className="text-center text-sm text-slate-500 mb-6">
                Le code expire dans <span className="font-medium">15 minutes</span>
              </p>

              {/* Verify Button */}
              <Button
                onClick={() => handleVerify()}
                disabled={loading || code.some(d => d === '')}
                className="w-full bg-gradient-to-r from-webtech-blue to-blue-600 hover:from-webtech-blue-hover hover:to-blue-700 shadow-lg h-12"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Vérifier mon email
                  </>
                )}
              </Button>

              {/* Resend Code */}
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500 mb-2">
                  Vous n'avez pas reçu le code ?
                </p>
                <Button
                  variant="ghost"
                  onClick={handleResendCode}
                  disabled={resending || countdown > 0}
                  className="text-webtech-blue hover:text-webtech-blue-hover"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : countdown > 0 ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Renvoyer dans {countdown}s
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Renvoyer le code
                    </>
                  )}
                </Button>
              </div>

              {/* Back to Registration */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <Link
                  to="/register"
                  className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Modifier mon email
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Trust signals */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Vos données sont protégées et sécurisées</span>
          </div>
        </div>
      </main>
    </div>
  );
}
